/**
 * History Store - Undo/Redo State Management
 *
 * This store manages undo/redo functionality by tracking snapshots
 * of the project state (nodes and their properties).
 *
 * HOW IT WORKS:
 * =============
 * - Whenever a significant change occurs (node moved, created, deleted, etc.),
 *   we push the current state onto the history stack
 * - Undo: Pop from history and restore that state
 * - Redo: Pop from redo stack and restore that state
 * - Max history size: 50 states to prevent memory issues
 *
 * IMAGE CLEANUP:
 * ==============
 * - When nodes with images are deleted, the image cleanup is deferred
 * - Images are only revoked when a new action clears the redo stack
 * - This allows undo to restore deleted nodes with their images intact
 */

import { create } from 'zustand';
import { BeatNodeData, TitleNode } from '@/types';
import { blobManager } from '@/utils/blobManager';
import { imageStore } from '@/utils/browserAPI';

// =============================================================================
// TYPES
// =============================================================================

/**
 * A snapshot of a single node's state
 */
export interface NodeSnapshot {
  id: string;
  position: { x: number; y: number };
  data: BeatNodeData;
}

/**
 * A snapshot of the entire project state
 */
export interface StateSnapshot {
  nodes: NodeSnapshot[];
  titleNodes?: TitleNode[];
  timestamp: number;
}

// =============================================================================
// STORE INTERFACE
// =============================================================================

interface HistoryStore {
  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------

  /** Stack of previous states (for undo) */
  past: StateSnapshot[];

  /** Stack of future states (for redo) */
  future: StateSnapshot[];

  /** Whether we're currently applying a history change (to prevent recursion) */
  isApplyingHistory: boolean;

  /** Image paths pending cleanup (will be revoked when redo stack is cleared) */
  pendingImageCleanup: Set<string>;

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  /**
   * Push current state onto the history stack
   * Call this before making a change that should be undoable
   */
  pushState: (snapshot: StateSnapshot) => void;

  /**
   * Undo the last change
   * Returns the state to restore, or null if nothing to undo
   */
  undo: () => StateSnapshot | null;

  /**
   * Redo the last undone change
   * Returns the state to restore, or null if nothing to redo
   */
  redo: () => StateSnapshot | null;

  /**
   * Clear all history (call when loading a new project)
   */
  clear: () => void;

  /**
   * Set whether we're applying history (internal use)
   */
  setApplyingHistory: (applying: boolean) => void;

  /**
   * Check if undo is available
   */
  canUndo: () => boolean;

  /**
   * Check if redo is available
   */
  canRedo: () => boolean;

  /**
   * Add a state to the future stack (for undo operations)
   */
  addToFuture: (snapshot: StateSnapshot) => void;

  /**
   * Add a state to the past stack (for redo operations)
   */
  addToPast: (snapshot: StateSnapshot) => void;

  /**
   * Add an image path to pending cleanup (will be cleaned when redo stack is cleared)
   */
  addPendingImageCleanup: (imagePath: string) => void;

  /**
   * Remove an image path from pending cleanup (called when undo restores the node)
   */
  removePendingImageCleanup: (imagePath: string) => void;

  /**
   * Execute pending image cleanup (called when redo stack is cleared)
   * Returns the list of image paths that should be cleaned up
   */
  executePendingImageCleanup: () => string[];
}

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

const MAX_HISTORY_SIZE = 50;

export const useHistoryStore = create<HistoryStore>((set, get) => ({
  // ---------------------------------------------------------------------------
  // INITIAL STATE
  // ---------------------------------------------------------------------------

  past: [],
  future: [],
  isApplyingHistory: false,
  pendingImageCleanup: new Set<string>(),

  // ---------------------------------------------------------------------------
  // ACTIONS
  // ---------------------------------------------------------------------------

  pushState: (snapshot) => {
    const { past, future, isApplyingHistory, executePendingImageCleanup } = get();

    // Don't track changes that are themselves history operations
    if (isApplyingHistory) return;

    // If we had a redo stack (future states), clear pending image cleanup
    // because those deletions can no longer be undone
    if (future.length > 0) {
      const imagesToCleanup = executePendingImageCleanup();
      if (imagesToCleanup.length > 0) {
        console.log('[HistoryStore] Clearing redo stack, cleaning up images:', imagesToCleanup);
      }
    }

    // Add to past, clear future (redo becomes invalid after new change)
    const newPast = [...past, snapshot];

    // Limit history size
    if (newPast.length > MAX_HISTORY_SIZE) {
      newPast.shift(); // Remove oldest
    }

    set({
      past: newPast,
      future: [], // Clear redo stack
    });
  },

  undo: () => {
    const { past } = get();

    if (past.length === 0) return null;

    // Pop the last state from past - this is what we restore
    const newPast = [...past];
    const stateToRestore = newPast.pop()!;

    // Note: We don't add anything to future here
    // The caller (projectStore) will add the current state to future
    set({
      past: newPast,
    });

    return stateToRestore;
  },

  redo: () => {
    const { future } = get();

    if (future.length === 0) return null;

    // Pop the last state from future - this is what we restore
    const newFuture = [...future];
    const stateToRestore = newFuture.pop()!;

    // Note: We don't add anything to past here
    // The caller (projectStore) will add the current state to past
    set({
      future: newFuture,
    });

    return stateToRestore;
  },

  clear: () => {
    const { executePendingImageCleanup } = get();
    // Clean up any pending images when clearing history
    executePendingImageCleanup();

    set({
      past: [],
      future: [],
      isApplyingHistory: false,
      pendingImageCleanup: new Set<string>(),
    });
  },

  setApplyingHistory: (applying) => {
    set({ isApplyingHistory: applying });
  },

  canUndo: () => {
    return get().past.length > 0;
  },

  canRedo: () => {
    return get().future.length > 0;
  },

  addToFuture: (snapshot) => {
    set((state) => ({
      future: [...state.future, snapshot],
    }));
  },

  addToPast: (snapshot) => {
    set((state) => ({
      past: [...state.past, snapshot],
    }));
  },

  addPendingImageCleanup: (imagePath) => {
    set((state) => {
      const newSet = new Set(state.pendingImageCleanup);
      newSet.add(imagePath);
      return { pendingImageCleanup: newSet };
    });
  },

  removePendingImageCleanup: (imagePath) => {
    set((state) => {
      const newSet = new Set(state.pendingImageCleanup);
      newSet.delete(imagePath);
      return { pendingImageCleanup: newSet };
    });
  },

  executePendingImageCleanup: () => {
    const { pendingImageCleanup } = get();
    const imagesToCleanup = Array.from(pendingImageCleanup);

    // Actually revoke the blob URLs
    for (const imagePath of imagesToCleanup) {
      if (imagePath.startsWith('node-image:')) {
        // Blob-managed image
        blobManager.revoke(imagePath);
        console.log('[HistoryStore] Revoked blob-managed image:', imagePath);
      } else {
        // Legacy imageStore path
        const blobUrl = imageStore.get(imagePath);
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
          imageStore.delete(imagePath);
          console.log('[HistoryStore] Revoked legacy image:', imagePath);
        }
      }
    }

    // Clear the set
    set({ pendingImageCleanup: new Set<string>() });

    return imagesToCleanup;
  },
}));
